<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useScreenSize } from '@/composables';
import { capitalizeWords } from '@/common';
import { getStopTime, getStopDay } from '@/utils';
import { ButtonComponent } from '@/components';

const router = useRouter();
const { innerWidth } = useScreenSize();
const { load } = defineProps({
  load: {
    type: Object,
    required: true,
  },
  shouldWrap: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['showAssignVehicleModal']);

const activePageTab = computed(
  () => router.currentRoute.value.query.tab || 'unassigned'
);

const getStatusClass = (status) => {
  switch (status) {
    case 'progress':
      return 'bg-ocean-100 text-ocean-800';
    case 'covered':
      return 'bg-sun-50 text-sun-800';
    case 'cancelled':
      return 'bg-steel-100 text-steel-800';
    case 'unassigned':
      return 'bg-rose-100 text-rose-800';
    default:
      return 'bg-forest-100 text-forest-800';
    //   default case is green/good
  }
};

const stopDetails = (stop) => {
  if (!stop) {
    return false;
  }
  return {
    id: stop.id,
    street1: capitalizeWords(stop.street1),
    street2: capitalizeWords(stop.street2),
    city: capitalizeWords(stop.city) || stop.area_five_zip_city || '',
    state: stop.state || stop.area_five_zip_state || '',
    zip: stop.postal_code || '',
    appointmentDay: getStopDay(
      stop.scheduled_window_started_at_in_stop_timezone ||
        stop.scheduled_window_ended_at_in_stop_timezone,
      stop.timezone,
      { monthNumeric: true, yearDigits: 2, includesYear: false }
    ),
    appointmentTime: getStopTime(
      stop.scheduled_window_started_at_in_stop_timezone ||
        stop.scheduled_window_ended_at_in_stop_timezone,
      stop.timezone
    ),
    stopDateRaw:
      stop.scheduled_window_started_at_in_stop_timezone ||
      stop.scheduled_window_ended_at_in_stop_timezone,
    timezone: stop.timezone,
    arrivalDay: getStopDay(stop.arrived_at_in_stop_timezone, stop.timezone),
    arrivalTime: getStopTime(stop.arrived_at_in_stop_timezone, stop.timezone),
    departureDay: getStopDay(stop.departed_at_in_stop_timezone, stop.timezone),
    departureTime: getStopTime(
      stop.departed_at_in_stop_timezone,
      stop.timezone
    ),
  };
};
const origin = computed(() =>
  load?.stops?.length
    ? stopDetails(load.stops[0])
    : { city: '-', appointmentDay: '-' }
);
const destination = computed(() =>
  load?.stops?.length > 1
    ? stopDetails(load.stops[load.stops.length - 1])
    : {
        city: '-',
        appointmentDay: origin.value.appointmentDay,
        appointmentTime: origin.value.appointmentTime,
      }
);

const displayOrigin = computed(() => {
  const { city, state } = origin.value;
  if (city === '-' || !city) return '-';
  return state ? `${city}, ${state}` : city;
});
const displayDestination = computed(() => {
  const { city, state } = destination.value;
  if (city === '-' || !city) return '-';
  return state ? `${city}, ${state}` : city;
});
</script>

<template>
  <div
    v-if="innerWidth > 0"
    class="flex min-w-full flex-col gap-2 rounded-lg border border-steel-300 bg-white shadow-md md:min-w-[315px]"
    :class="
      innerWidth < 400 ? 'w-full' : innerWidth < 610 ? 'w-[400px]' : 'w-[285px]'
    "
  >
    <div class="flex flex-col px-4 pb-1 pt-3">
      <div class="flex w-full items-center justify-between gap-1 pb-2">
        <p
          class="max-w-[275px] justify-start self-stretch text-left text-lg font-semibold text-steel-1100"
        >
          {{ load?.shipment_identifier || '' }}
        </p>
        <div
          v-if="activePageTab !== 'unassigned'"
          class="flex items-center justify-center gap-1 rounded-full px-1.5 py-0.5 text-xs capitalize"
          :class="getStatusClass(load?.status)"
        >
          {{ load?.status?.replace('_', ' ') }}
        </div>
      </div>
      <div
        class="relative flex h-[100px] flex-col items-start justify-center gap-2 self-stretch pb-2 pl-4"
      >
        <div class="flex w-full flex-1 items-center justify-between">
          <p class="text-left text-base font-bold text-steel-900">
            {{ displayOrigin }}
          </p>
          <p class="min-w-max text-xs font-normal text-steel-700">
            {{
              `${origin.appointmentDay}${origin.appointmentTime ? ` • ${origin.appointmentTime}` : ''}`
            }}
          </p>
        </div>

        <img
          class="absolute left-0 top-[22px] h-[50px] w-3"
          src="@/assets/loads/line-yellow.svg"
          alt="yellow icon"
        />

        <div class="flex w-full flex-1 items-center justify-between">
          <p class="text-base font-bold text-steel-900">
            {{ displayDestination }}
          </p>
          <p class="text-xs font-normal text-steel-700">
            {{
              `${destination.appointmentDay}${destination.appointmentTime ? ` • ${destination.appointmentTime}` : ''}`
            }}
          </p>
        </div>
      </div>

      <div class="flex gap-2 [&>*]:flex-1">
        <ButtonComponent
          class="border border-steel-300"
          @click="
            router.push({
              name: 'Load Detail',
              params: { id: load.id },
            })
          "
        >
          View Details
        </ButtonComponent>
        <ButtonComponent
          v-if="activePageTab === 'unassigned'"
          :variant="
            load?.carrier_vehicle?.id
              ? 'secondary'
              : load?.pending_driver_carrier_vehicle?.id
                ? 'default'
                : 'primary'
          "
          @click.stop="
            emit('showAssignVehicleModal', load, origin, destination)
          "
        >
          {{
            `${load?.carrier_vehicle?.id ? 'Confirm' : load?.pending_driver_carrier_vehicle?.id ? 'Change' : 'Assign'} Vehicle`
          }}
        </ButtonComponent>
      </div>
    </div>

    <div class="flex items-center gap-2 rounded-b-lg bg-steel-100 px-3 py-2">
      <div
        v-if="load?.billable_miles"
        class="flex items-center gap-1"
      >
        <img
          src="@/assets/loads/arrows-yellow.svg"
          alt="arrows"
        />
        <p class="text-xs font-normal text-steel-800">
          {{
            load?.billable_miles?.toLocaleString('en-US', {
              maximumFractionDigits: 2,
            })
          }}
          mi
        </p>
      </div>

      <div class="flex items-center gap-1">
        <img
          src="@/assets/loads/route-yellow.svg"
          alt="route"
        />
        <p class="text-xs font-normal text-steel-800">
          {{ load?.stops?.length }} stop{{ load?.stops?.length > 1 ? 's' : '' }}
        </p>
      </div>
    </div>
  </div>
</template>
